import { useEffect, useState, useCallback, useMemo, memo } from "react";
import { api } from "@/lib/api";
import { onWOChanged } from "@/lib/woEvents";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import CreateWODialog from "@/components/CreateWODialog";

const STATUS_COLORS = {
  NEW:         "bg-blue-50 text-blue-700 border-blue-200",
  SCHEDULED:   "bg-yellow-50 text-yellow-800 border-yellow-300",
  ASSIGNED:    "bg-purple-50 text-purple-700 border-purple-200",
  IN_PROGRESS: "bg-orange-50 text-orange-700 border-orange-200",
  DONE:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  CANCELLED:   "bg-zinc-100 text-zinc-600 border-zinc-300",
};

const PAGE_SIZE = 50;

// v83.7 — Baris tabel di-memoize supaya mengetik di dialog tidak re-render
// SEMUA baris (previously: 1 typing = seluruh tabel re-render).
const OrderRow = memo(function OrderRow({ o, techName, onSelect, onReassign, onStatus }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-zinc-50/60 cursor-pointer"
        onClick={() => onSelect(o)} data-testid={`wo-row-${o.id}`}>
      <td className="px-4 py-3">
        <div className="font-semibold">{o.customer_display_name || o.customer_name}</div>
        <div className="text-[11px] text-zinc-500">{o.customer_phone}</div>
        <div className="text-[11px] text-zinc-500 truncate max-w-xs">{o.address}</div>
      </td>
      <td className="px-4 py-3 font-mono text-[12px]">{o.service_type}</td>
      <td className="px-4 py-3 font-mono text-[12px]">
        {o.scheduled_start ? (
          <>{o.scheduled_start.slice(0,10)}<br/><span className="text-zinc-500">{o.scheduled_start.slice(11,16)}–{o.scheduled_end?.slice(11,16)}</span></>
        ) : <span className="text-zinc-400">—</span>}
      </td>
      <td className="px-4 py-3">{techName(o.technician_id)}</td>
      <td className="px-4 py-3">
        {/* v84.11 — WO status NEW tanpa GCal event → tampilkan "Belum Terjadwal" */}
        {(o.status === "NEW" && !o.google_event_id) ? (
          <Badge className="rounded-sm border bg-amber-50 text-amber-800 border-amber-300"
                 data-testid={`wo-badge-belum-terjadwal-${o.id}`}>
            Belum Terjadwal
          </Badge>
        ) : (
          <Badge className={`rounded-sm border ${STATUS_COLORS[o.status]}`}>{o.status}</Badge>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
          <Button variant="outline" size="sm" onClick={() => onReassign(o.id)} className="rounded-sm h-7 text-[11px]" data-testid={`reassign-${o.id}`}>Re-assign</Button>
          <Select onValueChange={v => onStatus(o.id, v)}>
            <SelectTrigger className="h-7 w-28 rounded-sm text-[11px]" data-testid={`status-${o.id}`}><SelectValue placeholder="Ubah…" /></SelectTrigger>
            <SelectContent>{Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </td>
    </tr>
  );
});

// v84.34 — Halaman list WO — dialog create dishared di CreateWODialog.jsx


export default function WorkOrders() {
  const [orders, setOrders] = useState([]);
  const [techs, setTechs] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);          // v83.7 — pagination
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    const params = {
      status: filter === "ALL" ? undefined : filter,
      limit: PAGE_SIZE, skip: page * PAGE_SIZE,
    };
    const [o, c, t] = await Promise.all([
      api.listOrders(params),
      api.countOrders({ status: params.status }),
      techs.length ? Promise.resolve(techs) : api.listTechs(),
    ]);
    setOrders(o); setTotal(c.count || 0);
    if (!techs.length) setTechs(t);
  }, [filter, page, techs.length]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [filter, page]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [filter]);
  // v84.31 — Subscribe global WO event supaya list terupdate saat WO dibuat/edit
  // di halaman lain (mis. CalendarBoard di dashboard).
  useEffect(() => {
    const off = onWOChanged(() => { load(); });
    return off;
  }, [load]);

  const techName = useCallback(
    (id) => techs.find(t => t.id === id)?.name || "-",
    [techs]
  );
  const handleReassign = useCallback(async (id) => {
    const r = await api.reassign(id);
    if (r.ok) toast.success("Re-assigned");
    else toast.error(r.error || "Tidak ada teknisi tersedia");
    load();
  }, [load]);
  const handleStatus = useCallback(async (id, status) => {
    await api.updateStatus(id, status);
    toast.success(`Status → ${status}`);
    load();
  }, [load]);
  const handleSelect = useCallback((o) => setSelected(o), []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rows = useMemo(() => orders.map(o => (
    <OrderRow key={o.id} o={o} techName={techName}
      onSelect={handleSelect} onReassign={handleReassign} onStatus={handleStatus} />
  )), [orders, techName, handleSelect, handleReassign, handleStatus]);

  return (
    <div className="space-y-4" data-testid="workorders-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 rounded-sm h-9" data-testid="wo-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua status</SelectItem>
              {Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} className="rounded-sm gap-1.5"><RefreshCw className="w-3.5 h-3.5" />Refresh</Button>
        </div>
        <Button onClick={() => setOpen(true)}
          className="bg-[#E65100] hover:bg-[#F57C00] rounded-sm gap-1.5"
          data-testid="new-wo-btn">
          <Plus className="w-4 h-4" />Work Order Baru
        </Button>
      </div>

      <CreateWODialog
        open={open}
        onOpenChange={setOpen}
        techs={techs}
        onCreated={load}
      />

      <div className="border border-border rounded-sm overflow-hidden bg-white">
        <table className="w-full text-[13px]">
          <thead className="bg-zinc-50 text-[11px] uppercase tracking-widest text-zinc-500">
            <tr className="text-left">
              <th className="px-4 py-2.5">Customer</th>
              <th className="px-4 py-2.5">Layanan</th>
              <th className="px-4 py-2.5">Jadwal</th>
              <th className="px-4 py-2.5">Teknisi</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {rows}
            {!orders.length && <tr><td colSpan="6" className="text-center py-8 text-zinc-500">Belum ada work order.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* v83.7 — Pagination footer */}
      <div className="flex items-center justify-between text-[12px] text-zinc-600">
        <div>
          Menampilkan <b>{orders.length}</b> dari <b>{total}</b> WO
          {totalPages > 1 && <> · Halaman <b>{page + 1}</b> / <b>{totalPages}</b></>}
        </div>
        {totalPages > 1 && (
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0} className="rounded-sm h-8 gap-1"
              data-testid="wo-page-prev">
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1} className="rounded-sm h-8 gap-1"
              data-testid="wo-page-next">
              Next <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      {selected && (
        <Dialog open={!!selected} onOpenChange={()=>setSelected(null)}>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader><DialogTitle>Detail Work Order · {selected.customer_display_name || selected.customer_name}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-[13px]">
              <div><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Alamat</span><div>{selected.address}</div></div>
              <div><span className="text-zinc-500 text-[11px] uppercase tracking-widest">Deskripsi</span><div>{selected.description || "—"}</div></div>
              {selected.ai_reasoning && (
                <div>
                  <span className="text-zinc-500 text-[11px] uppercase tracking-widest">AI Reasoning</span>
                  <pre className="terminal p-3 rounded-sm mt-1 whitespace-pre-wrap max-h-64 overflow-auto">{selected.ai_reasoning}</pre>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
